import { cn } from '@invent/wl-ui-kit-next';
import * as React from 'react';

import { SplitToggle, useSplit } from './split-view';
import { useDocumentTitle, useZone } from './zone';

/**
 * A screen's title block, in its zone's density (the two-portals design, §5).
 *
 * - **Portal:** spacious — a light 36 px title in Royal Blue with a lede under it, on the
 *   off-white ground, left-aligned to the page's reading column. Home's gradient hero is its own
 *   component; every other portal screen opens with this.
 * - **Team:** compact — the area as a breadcrumb line, a 22 px medium title, the record's key
 *   chips, its actions at the right, and its routed tabs along the bottom edge.
 *
 * The title is the page's one `h1`, and the tab's title unless `documentTitle` says otherwise
 * (a record still loading reads its placeholder in both). The breadcrumb is text, not a link: every
 * screen that has somewhere to go back to already draws its own way back, and two of them side by
 * side would disagree the day one is changed.
 *
 * `media` is a picture the record is known by — a participant's photo — drawn before the title
 * block. It is decoration beside the `h1`, never a second name for it.
 *
 * Inside a record drawn beside its list (`SplitView`), the Team Portal's header carries the list's
 * switch — "Show list" or "Hide list" — on its crumb line.
 *
 * **A work page's row** (`row`, Team only, SR-485): no crumb — the rail says the section — the
 * title with its tabs beside it and the actions at the row's right, wrapping on a narrow window, and
 * the page's summary (`line`) on a quiet line under them. Scrolled away, the page's compact bar
 * (`condense.tsx`) stands for it in the app bar's row.
 *
 * It replaces `PageTitleBar`, the Royal Blue → Sapphire banner every screen of both audiences
 * opened with. The banner survives only as the portal Home's hero, which is the one place the
 * guidelines' gradient now stands for the Advisor Portal.
 */
export function PageHeader({
  title,
  documentTitle,
  crumb,
  lede,
  meta,
  media,
  actions,
  tabs,
  row = false,
  line,
  className
}: {
  title: React.ReactNode;
  /** The tab's title, when `title` is not plain text. */
  documentTitle?: string;
  crumb?: React.ReactNode;
  lede?: React.ReactNode;
  /** Team only: the chips a record is known by at a glance. */
  meta?: React.ReactNode;
  /** Team only: a picture before the title block — decorative, the `h1` names the record. */
  media?: React.ReactNode;
  actions?: React.ReactNode;
  /** Team only: the routed tabs, drawn on the header's bottom edge. */
  tabs?: React.ReactNode;
  /** Team only: a work page's one row — the tabs beside the title. */
  row?: boolean;
  /** With `row`: the page's summary, on a line under the row. */
  line?: React.ReactNode;
  className?: string;
}) {
  const zone = useZone();
  const split = useSplit();
  useDocumentTitle(documentTitle ?? (typeof title === 'string' ? title : null));

  if (zone === 'team' && row) {
    return <WorkHeader title={title} tabs={tabs} actions={actions} line={line} className={className} />;
  }

  if (zone === 'team') {
    return (
      <header className={cn('border-b border-border bg-white px-6 pt-4', !tabs && 'pb-4', className)}>
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="flex min-w-0 items-center gap-3">
            {media}
            <div className="min-w-0">
              {(crumb || split) && (
                <div className="mb-0.5 flex min-h-5 items-center gap-2">
                  <SplitToggle />
                  {crumb && <p className="text-xs text-muted-foreground">{crumb}</p>}
                </div>
              )}
              <h1 className="text-[1.375rem] leading-tight font-medium tracking-[-0.005em] break-words text-[var(--color-royal-blue)]">
                {title}
              </h1>
              {lede && (
                <p className="mt-1 max-w-3xl text-[0.8125rem] leading-relaxed text-muted-foreground">
                  {lede}
                </p>
              )}
            </div>
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {meta && <div className="mt-2.5 flex flex-wrap items-center gap-1.5">{meta}</div>}
        {tabs && <div className="mt-3">{tabs}</div>}
      </header>
    );
  }

  return (
    <header className={cn('mx-auto w-full max-w-[1120px] px-6 pt-10 pb-2 lg:px-10', className)}>
      {crumb && <p className="mb-1 text-[0.9375rem] text-muted-foreground">{crumb}</p>}
      <h1 className="text-[2rem] leading-tight font-light tracking-[-0.01em] break-words text-[var(--color-royal-blue)] sm:text-[2.25rem]">
        {title}
      </h1>
      {lede && (
        <p className="mt-3 max-w-[40em] text-[1.0625rem] leading-relaxed text-muted-foreground">
          {lede}
        </p>
      )}
      {actions && <div className="mt-5 flex flex-wrap items-center gap-3">{actions}</div>}
    </header>
  );
}

/** `PageHeader`'s work-page row. */
function WorkHeader({
  title,
  tabs,
  actions,
  line,
  className
}: {
  title: React.ReactNode;
  tabs?: React.ReactNode;
  actions?: React.ReactNode;
  line?: React.ReactNode;
  className?: string;
}) {
  return (
    <header className={cn('border-b border-border bg-white px-6 pt-3 pb-2.5', className)}>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-6 gap-y-1">
          <h1 className="text-[1.375rem] leading-tight font-medium tracking-[-0.005em] break-words text-[var(--color-royal-blue)]">
            {title}
          </h1>
          {tabs}
        </div>
        {actions && <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {line && <div className="mt-2 text-sm">{line}</div>}
    </header>
  );
}
